/**
 * Boost PFS Subscription Button Handler
 * Conditionally shows/hides subscription buttons based on product selling plan groups
 * Optimized version with batching, debouncing, and improved performance
 */

class BoostPFSSubscriptionHandler {
  constructor() {
    this.productCache = new Map();
    this.pendingRequests = new Map();
    this.processedButtons = new WeakSet();
    this.batchTimeout = null;
    this.batchQueue = [];
    this.recheckTimeout = null;
    
    // Configuration
    this.config = {
      batchDelay: 50,
      recheckDelay: 100,
      maxConcurrentRequests: 3,
      cacheExpiry: 300000 // 5 minutes
    };
    
    this.init();
  }

  init() {
    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.handleSubscriptionButtons());
    } else {
      this.handleSubscriptionButtons();
    }

    // Handle dynamic content updates (when filters are applied)
    this.observeBoostPFSUpdates();
  }

  /**
   * Main function to handle all subscription buttons on the page
   */
  handleSubscriptionButtons() {
    const subscriptionButtons = document.querySelectorAll('.product-grid-item-subscribe-btn[data-product-handle]');
    
    // Filter out already processed buttons and collect unique product handles
    const uniqueHandles = new Set();
    const buttonsToProcess = [];
    
    subscriptionButtons.forEach(button => {
      if (!this.processedButtons.has(button) && button.dataset) {
        const productHandle = button.dataset.productHandle;
        if (productHandle && productHandle.trim()) {
          uniqueHandles.add(productHandle);
          buttonsToProcess.push({ button, productHandle });
          this.processedButtons.add(button);
        }
      }
    });

    if (buttonsToProcess.length === 0) return;

    // Process buttons in batches
    this.processBatch(buttonsToProcess);
  }

  /**
   * Process buttons in batches to optimize performance
   */
  processBatch(buttonsToProcess) {
    // Group buttons by product handle
    const buttonsByHandle = new Map();
    
    buttonsToProcess.forEach(({ button, productHandle }) => {
      if (!buttonsByHandle.has(productHandle)) {
        buttonsByHandle.set(productHandle, []);
      }
      buttonsByHandle.get(productHandle).push(button);
    });

    // Process each unique product handle
    buttonsByHandle.forEach((buttons, productHandle) => {
      this.checkProductSellingPlans(productHandle, buttons);
    });
  }

  /**
   * Check if a product has selling plan groups (optimized version)
   */
  async checkProductSellingPlans(productHandle, buttons) {
    try {
      // Check cache first (with expiry)
      const cached = this.getCachedResult(productHandle);
      if (cached !== null) {
        buttons.forEach(button => this.toggleSubscriptionButton(button, cached));
        return;
      }

      // Check if request is already pending
      if (this.pendingRequests.has(productHandle)) {
        const existingPromise = this.pendingRequests.get(productHandle);
        const hasSellingPlans = await existingPromise;
        buttons.forEach(button => this.toggleSubscriptionButton(button, hasSellingPlans));
        return;
      }

      // Create new request
      const requestPromise = this.fetchProductData(productHandle);
      this.pendingRequests.set(productHandle, requestPromise);

      try {
        const hasSellingPlans = await requestPromise;
        
        // Cache the result with timestamp
        this.setCachedResult(productHandle, hasSellingPlans);
        
        // Update all buttons for this product
        buttons.forEach(button => this.toggleSubscriptionButton(button, hasSellingPlans));
        
      } finally {
        // Clean up pending request
        this.pendingRequests.delete(productHandle);
      }
      
    } catch (error) {
      console.error(`Error checking selling plans for product ${productHandle}:`, error);
      buttons.forEach(button => this.hideSubscriptionButton(button));
      this.pendingRequests.delete(productHandle);
    }
  }

  /**
   * Fetch product data from Shopify API
   */
  async fetchProductData(productHandle) {
    const response = await fetch(`/products/${productHandle}.js`);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch product data for ${productHandle}: ${response.status}`);
    }

    const productData = await response.json();
    return productData.selling_plan_groups && productData.selling_plan_groups.length > 0;
  }

  /**
   * Get cached result with expiry check
   */
  getCachedResult(productHandle) {
    const cached = this.productCache.get(productHandle);
    if (!cached) return null;
    
    const { result, timestamp } = cached;
    const now = Date.now();
    
    if (now - timestamp > this.config.cacheExpiry) {
      this.productCache.delete(productHandle);
      return null;
    }
    
    return result;
  }

  /**
   * Set cached result with timestamp
   */
  setCachedResult(productHandle, result) {
    this.productCache.set(productHandle, {
      result,
      timestamp: Date.now()
    });
  }

  /**
   * Show or hide subscription button based on selling plans
   */
  toggleSubscriptionButton(button, hasSellingPlans) {
    if (hasSellingPlans) {
      this.showSubscriptionButton(button);
    } else {
      this.hideSubscriptionButton(button);
    }
  }

  /**
   * Show subscription button
   */
  showSubscriptionButton(button) {
    button.style.display = '';
    button.setAttribute('aria-hidden', 'false');
  }

  /**
   * Hide subscription button
   */
  hideSubscriptionButton(button) {
    button.style.display = 'none';
    button.setAttribute('aria-hidden', 'true');
  }

  /**
   * Observe for Boost PFS content updates (optimized)
   */
  observeBoostPFSUpdates() {
    // Use a single observer for better performance
    const targetSelectors = [
      '.boost-pfs-filter-products',
      '.boost-pfs-filter-product-item-grid', 
      '.boost-pfs-filter-product-item-list',
      '#boost-pfs-filter-products'
    ];
    
    const targets = document.querySelectorAll(targetSelectors.join(', '));
    if (targets.length === 0) return;

    const observer = new MutationObserver(this.debounce((mutations) => {
      if (this.shouldProcessMutations(mutations)) {
        this.handleSubscriptionButtons();
      }
    }, this.config.recheckDelay));

    // Observe all targets with a single observer
    targets.forEach(target => {
      observer.observe(target, {
        childList: true,
        subtree: true
      });
    });

    // Store observer for cleanup if needed
    this.observer = observer;
  }

  /**
   * Check if mutations contain relevant changes
   */
  shouldProcessMutations(mutations) {
    return mutations.some(mutation => {
      if (mutation.type !== 'childList' || mutation.addedNodes.length === 0) {
        return false;
      }
      
      return Array.from(mutation.addedNodes).some(node => {
        if (node.nodeType !== Node.ELEMENT_NODE) return false;
        
        // Check if the node or its descendants contain subscription buttons
        return node.matches && (
          node.matches('.product-grid-item-subscribe-btn[data-product-handle]') ||
          node.querySelector('.product-grid-item-subscribe-btn[data-product-handle]') ||
          node.matches('.boost-pfs-filter-product-item, .product-grid-item')
        );
      });
    });
  }

  /**
   * Debounce utility function
   */
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func.apply(this, args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  /**
   * Cleanup method for removing observers and clearing caches
   */
  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
    
    clearTimeout(this.batchTimeout);
    clearTimeout(this.recheckTimeout);
    
    this.productCache.clear();
    this.pendingRequests.clear();
    this.processedButtons = new WeakSet();
  }
}

// Initialize when the script loads
const subscriptionHandler = new BoostPFSSubscriptionHandler();

// Also expose globally for manual initialization if needed
if (typeof window !== 'undefined') {
  window.BoostPFSSubscriptionHandler = BoostPFSSubscriptionHandler;
  window.subscriptionHandler = subscriptionHandler;
}
